import { Undo2 } from "lucide-react";
import type { Matter } from "@/shared/cache/slices/matters";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import type { Entry } from "@/shared/types/entries";
import { DateBar } from "@/v2/components/DateBar";
import { FilterPopover, type FilterState } from "@/v2/components/FilterPopover";
import {
	type GroupMode,
	type SortMode,
	SortPopover,
} from "@/v2/components/SortPopover";
import { ButtonGroup } from "@/v2/components/ui/button-group";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { useActions } from "@/v2/contexts/EntriesActionsProvider";
import type { DateRange } from "@/v2/hooks/useDateParams";
import { useEntriesUndo } from "@/v2/undo";

export interface EntriesDateToolbarProps {
	date: Date;
	dateRange: DateRange;
	onDateChange: (date: Date) => void;
	onDateRangeChange: (dateRange: DateRange) => void;
	filter: FilterState;
	onFilterChange: (filter: FilterState) => void;
	sort: SortMode;
	group: GroupMode;
	onSortChange: (sort: SortMode) => void;
	onGroupChange: (group: GroupMode) => void;
	entries: Entry[];
	matters?: Matter[];
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
}

export function EntriesDateToolbar({
	date,
	dateRange,
	onDateChange,
	onDateRangeChange,
	filter,
	onFilterChange,
	sort,
	group,
	onSortChange,
	onGroupChange,
	entries,
	matters,
	getRestrictedWords,
}: EntriesDateToolbarProps) {
	const { queueSize } = useActions();
	const { canUndo, undoLabel, isUndoing, isMutating, undo } = useEntriesUndo();

	return (
		<div className="sticky top-0 z-20 border-b border-border bg-background">
			<DateBar
				date={date}
				dateRange={dateRange}
				onDateChange={onDateChange}
				onDateRangeChange={onDateRangeChange}
				leftSlot={
					<div className="flex items-center gap-1">
						<ButtonGroup>
							<TooltipButton
								variant="outline"
								size="icon"
								aria-label={undoLabel ? `Undo ${undoLabel}` : "Undo"}
								tooltip={undoLabel ? `Undo ${undoLabel}` : "Undo"}
								onClick={() => void undo()}
								disabled={!canUndo || isUndoing || isMutating || queueSize > 0}
							>
								<Undo2 />
							</TooltipButton>
						</ButtonGroup>
						<FilterPopover
							filter={filter}
							onChange={onFilterChange}
							entries={entries}
							matters={matters}
							getRestrictedWords={getRestrictedWords}
						/>
					</div>
				}
				rightSlot={
					<SortPopover
						sort={sort}
						group={group}
						onSortChange={onSortChange}
						onGroupChange={onGroupChange}
					/>
				}
			/>
		</div>
	);
}
